import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateScimIntegrationRequest,
  UpdateScimIntegrationRequest,
} from 'src/api/FiretrolApiClient';
import { scimIntegrationApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

export function useCreateScimIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateScimIntegrationRequest) =>
      scimIntegrationApiClient.create(request),
    meta: { successMessage: 'Integration created!', suppressErrorToast: true },
    onSettled: () => invalidateFor('scim.save', qc),
  });
}

export interface UpdateScimIntegrationArgs {
  id: string;
  request: UpdateScimIntegrationRequest;
}

export function useUpdateScimIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: UpdateScimIntegrationArgs) =>
      scimIntegrationApiClient.update(id, request),
    meta: { successMessage: 'Integration updated!', suppressErrorToast: true },
    onSettled: () => invalidateFor('scim.save', qc),
  });
}

export function useDisableScimIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scimIntegrationApiClient.disable(id),
    meta: {
      successMessage: 'Integration disabled!',
      errorContext: 'disable SCIM integration',
    },
    onSettled: () => invalidateFor('scim.save', qc),
  });
}

export function useDeleteScimIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scimIntegrationApiClient.delete(id),
    meta: {
      successMessage: 'Integration deleted!',
      errorContext: 'delete SCIM integration',
    },
    onSettled: () => invalidateFor('scim.save', qc),
  });
}

/**
 * A connectivity probe: the server reports failure in the payload rather than
 * by status code, so the caller renders the outcome itself.
 */
export function useTestScimIntegration() {
  return useMutation({
    mutationFn: (id: string) => scimIntegrationApiClient.test(id),
    meta: { errorContext: 'SCIM test connection' },
  });
}

export function useScimIntegrationFullSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scimIntegrationApiClient.fullSync(id),
    meta: { errorContext: 'SCIM full sync' },
    onSettled: () => invalidateFor('scim.save', qc),
  });
}
